import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../../lib/api';
import { fetchOpenDatesForStaffReserve } from '../../utils/reservation/by-staff/fetchOpenDatesForStaffReserve';
import { fetchAvailableTimesByStaff } from '../../utils/reservation/by-staff/fetchAvailableTimesByStaff';

type RescheduleReservation = {
  id: number;
  store_id: number;
  staff_id: number | null;
  first_service_id?: number | null;
  service_count?: number;
  duration_minutes: number;
  reservation_date: string;
  start_time: string;
  end_time: string;
};

type Props = {
  open: boolean;
  reservation: RescheduleReservation | null;
  onClose: () => void;
  onSuccess: () => void;
};

const RescheduleReservationModal = ({ open, reservation, onClose, onSuccess }: Props) => {
  const [openDates, setOpenDates] = useState<string[]>([]);
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [availableTimes, setAvailableTimes] = useState<string[]>([]);
  const [loadingDates, setLoadingDates] = useState(false);
  const [loadingTimes, setLoadingTimes] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open || !reservation?.store_id || !reservation?.staff_id) {
      return;
    }

    setLoadingDates(true);
    setDate(reservation.reservation_date);
    setTime(reservation.start_time);
    setAvailableTimes([]);

    fetchOpenDatesForStaffReserve(reservation.store_id, reservation.staff_id, 90)
      .then((dates) => {
        const nextDates = Array.isArray(dates) ? dates : [];
        setOpenDates(nextDates);
        if (!nextDates.includes(reservation.reservation_date) && nextDates[0]) {
          setDate(nextDates[0]);
          setTime('');
        }
      })
      .catch(() => {
        toast.error('Failed to load available dates');
      })
      .finally(() => setLoadingDates(false));
  }, [open, reservation]);

  useEffect(() => {
    if (!open || !reservation?.store_id || !reservation?.staff_id || !date || !reservation.duration_minutes) {
      return;
    }

    setLoadingTimes(true);
    fetchAvailableTimesByStaff({
      storeId: reservation.store_id,
      staffId: reservation.staff_id,
      date,
      durationMinutes: reservation.duration_minutes,
      serviceIds:
        reservation.service_count === 1 && reservation.first_service_id
          ? [reservation.first_service_id]
          : undefined,
      excludeReservationId: reservation.id,
    })
      .then((slots) => {
        const nextTimes = slots.filter((slot) => slot.available).map((slot) => slot.time);
        setAvailableTimes(nextTimes);
        setTime((prev) => (nextTimes.includes(prev) ? prev : (nextTimes[0] ?? '')));
      })
      .catch(() => {
        toast.error('Failed to load available times');
      })
      .finally(() => setLoadingTimes(false));
  }, [date, open, reservation]);

  if (!open || !reservation) return null;

  const sameAsCurrent = date === reservation.reservation_date && time === reservation.start_time;

  const handleSubmit = async () => {
    if (!date || !time) {
      toast.error('Please select both a date and time');
      return;
    }

    if (sameAsCurrent) {
      toast.error('Please choose a different date or time');
      return;
    }

    setSubmitting(true);
    try {
      await api.patch(`/api/customer-app/reservation/reschedule/${reservation.id}`, {
        date,
        start_time: time,
      });
      toast.success('Reservation updated');
      onSuccess();
      onClose();
    } catch (error: any) {
      const message =
        error?.response?.data?.error ||
        error?.message ||
        'Failed to update reservation';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-slate-900/40 px-4 py-6 sm:items-center"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-[28px] bg-white p-5 shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="mx-auto mb-4 h-1.5 w-14 rounded-full bg-slate-200" />

        <div className="flex items-start justify-between gap-3">
          <div className="space-y-2">
            <h3 className="text-lg font-semibold text-slate-900">Change date and time</h3>
            <p className="text-sm leading-6 text-slate-600">
              You can move this booking to another open slot with the same staff and services.
            </p>
            <p className="text-xs text-slate-500">
              Changes are available up to 24 hours before the booking time.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 text-slate-500 transition hover:bg-slate-50 hover:text-slate-700"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="mt-5 space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Date</label>
            <select
              value={date}
              onChange={(e) => setDate(e.target.value)}
              disabled={loadingDates || submitting}
              className="w-full rounded-2xl border border-slate-300 px-3 py-3 text-sm text-slate-900"
            >
              {!openDates.length && <option value="">{loadingDates ? 'Loading dates...' : 'No available dates'}</option>}
              {openDates.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Time</label>
            <select
              value={time}
              onChange={(e) => setTime(e.target.value)}
              disabled={loadingTimes || submitting || !date}
              className="w-full rounded-2xl border border-slate-300 px-3 py-3 text-sm text-slate-900"
            >
              {!availableTimes.length && (
                <option value="">
                  {loadingTimes ? 'Loading times...' : 'No available time'}
                </option>
              )}
              {availableTimes.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="mt-6 flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="flex-1 rounded-2xl border border-slate-300 px-4 py-3 text-sm font-semibold text-slate-700"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting || sameAsCurrent || !date || !time}
            className="flex-1 rounded-2xl bg-emerald-600 px-4 py-3 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:bg-slate-300"
          >
            {submitting ? 'Saving...' : 'Save new time'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RescheduleReservationModal;
